import { Check } from '@mui/icons-material';
import { Alert, Box, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import uniqid from 'uniqid';
import { SelfOrderPoint } from '../../apiTypes';
import EditModal from '../../components/EditModal';
import FormSelect from '../../components/forms/FormSelect';
import FormTextField from '../../components/forms/FormTextField';
import Loader from '../../components/Loader';
import PrimaryButton from '../../components/PrimaryButton';
import StyledTextField from '../../components/styled/StyledTextField';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { useSnackbar } from '../../contexts/snackbar.context';
import { deleteDoc, setDoc, updateDoc } from '../../utils/api';

type Props = {
  open: boolean;
  selfOrderPoint?: DbDocument<SelfOrderPoint> | null;
  onClose: () => void;
};

const EditSelfOrderPoint = ({ selfOrderPoint, open, onClose }: Props) => {
  const { cafeSlug, cafe } = useAdmin();
  const { stores, storesMap, wallets, walletsMap } = useCafe();
  const { lang } = useLocale();
  const { addSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [linked, setLinked] = useState(false);

  const storeIds = useMemo(() => stores.map((s) => s.id), [stores]);
  const walletIds = useMemo(() => wallets.map((s) => s.id), [wallets]);

  const form = useForm<SelfOrderPoint>({
    mode: 'onChange',
    defaultValues: {
      name: selfOrderPoint?.name || '',
      storeId: selfOrderPoint?.storeId || '',
      walletId: selfOrderPoint?.walletId || '',
      checkboxSettings: selfOrderPoint?.checkboxSettings || {
        cashierName: '',
        cashierToken: '',
        key: '',
      },
    },
  });

  const { watch, setValue } = form;
  const checkboxSettings = watch('checkboxSettings');

  const handleClose = () => {
    onClose();
  };

  const saveChanges = (newSelfOrderPoint: SelfOrderPoint) => {
    if (selfOrderPoint?.id) {
      updateDoc<SelfOrderPoint>(`cafes/${cafeSlug}/selfOrderPoints/${selfOrderPoint.id}`, newSelfOrderPoint);
    } else {
      const id = uniqid();
      setDoc<SelfOrderPoint>(`cafes/${cafeSlug}/selfOrderPoints/${id}`, newSelfOrderPoint);
    }
    onClose();
  };

  const loginCashier = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch('https://api.checkbox.in.ua/api/v1/cashier/signin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Client-Name': 'FireMenu',
          'X-Client-Version': 'v0.1.1',
        },
        body: JSON.stringify({ login, password }),
      });

      const resJson = await res.json();

      if (res.status !== 200) {
        addSnackbar(resJson.message || 'Упс, щось пішло не так', 'error');
        return;
      }

      const res2 = await fetch('https://api.checkbox.in.ua/api/v1/cashier/me', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${resJson.access_token}`,
          'X-Client-Name': 'FireMenu',
          'X-Client-Version': 'v0.1.1',
        },
      });

      const res2Json = await res2.json();

      setValue('checkboxSettings.cashierToken', resJson.access_token || '', { shouldDirty: true });
      setValue('checkboxSettings.cashierName', res2Json.full_name || '', { shouldDirty: true });
    } catch (e) {
      addSnackbar('Упс, щось пішло не так', 'error');
    } finally {
      setLoading(false);
    }
  }, [addSnackbar, login, password, setValue]);

  const logoutCashier = useCallback(() => {
    setValue('checkboxSettings.cashierToken', '', { shouldDirty: true });
    setValue('checkboxSettings.cashierName', '', { shouldDirty: true });
  }, [setValue]);

  const checkLink = useCallback(async () => {
    if (checkboxSettings.key && checkboxSettings.cashierToken) {
      setLoading(true);
      const res = await fetch('https://api.checkbox.in.ua/api/v1/webhook', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${checkboxSettings.cashierToken}`,
          'X-License-Key': checkboxSettings.key,
          'X-Client-Name': 'FireMenu',
          'X-Client-Version': 'v0.1.1',
        },
      });

      const payload = await (res.json() as unknown as Promise<{ url: string } | null>);

      if (payload && 'url' in payload && payload.url) {
        setLinked(true);
      } else {
        setLinked(false);
      }

      setLoading(false);
    } else {
      setLinked(false);
    }
  }, [checkboxSettings.cashierToken, checkboxSettings.key]);

  const setWebhook = useCallback(async () => {
    if (checkboxSettings.key && checkboxSettings.cashierToken && selfOrderPoint?.id) {
      setLoading(true);
      const res = await fetch('https://api.checkbox.in.ua/api/v1/webhook', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${checkboxSettings.cashierToken}`,
          'X-License-Key': checkboxSettings.key,
          'X-Client-Name': 'FireMenu',
          'X-Client-Version': 'v0.1.1',
        },
        body: JSON.stringify({
          url: `https://checkbox-rhbaprnhoq-ey.a.run.app/?cafeId=${cafeSlug}&point=${selfOrderPoint.id}`,
        }),
      });

      const payload = await (res.json() as unknown as Promise<{ url: string } | null>);

      if (payload && 'url' in payload && payload.url) {
        setLinked(true);
      } else {
        setLinked(false);
      }

      setLoading(false);
    } else {
      setLinked(false);
    }
  }, [cafeSlug, checkboxSettings.cashierToken, checkboxSettings.key, selfOrderPoint?.id]);

  const removeWebhook = useCallback(async () => {
    setLoading(true);
    await fetch('https://api.checkbox.in.ua/api/v1/webhook', {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${checkboxSettings.cashierToken}`,
        'X-License-Key': checkboxSettings.key,
        'X-Client-Name': 'FireMenu',
        'X-Client-Version': 'v0.1.1',
      },
    });
    setLinked(false);
    setLoading(false);
  }, [checkboxSettings.cashierToken, checkboxSettings.key]);

  const removeProduct = async () => {
    if (selfOrderPoint?.id) {
      await removeWebhook();
      deleteDoc(`cafes/${cafeSlug}/selfOrderPoints/${selfOrderPoint.id}`);
      onClose();
    }
  };

  useEffect(() => {
    checkLink();
  }, [checkLink]);

  return (
    <EditModal<SelfOrderPoint>
      title={t.self_order_point[lang]}
      open={open}
      onClose={handleClose}
      submitButton={selfOrderPoint ? t.save_changes[lang] : t.add[lang]}
      onSubmit={saveChanges}
      secondaryButton={selfOrderPoint ? t.remove[lang] : undefined}
      confirmText={t.remove_self_order_point_confirm[lang]}
      onSecondaryButtonClick={removeProduct}
      form={form}
    >
      <Box sx={{ py: 2 }}>
        <FormTextField name="name" label={t.name[lang]} rules={{ required: true }} fullWidth sx={{ mb: 3 }} />

        <FormSelect
          name="storeId"
          label={t.store[lang]}
          options={storeIds}
          renderOption={(id) => storesMap[id]?.name || id}
          sx={{ mb: 3 }}
        />

        <FormSelect
          name="walletId"
          label={t.wallet[lang]}
          options={walletIds}
          renderOption={(id) => walletsMap[id]?.name || id}
          sx={{ mb: 3 }}
        />

        {selfOrderPoint?.id && (
          <Box sx={{ p: 2, bgcolor: (t) => `${t.palette.secondary.light}22`, borderRadius: 1 }}>
            <Typography sx={{ color: (t) => t.palette.secondary.main, textAlign: 'center', mb: 2 }}>
              Зв'язок з Checkbox
            </Typography>

            <Alert severity={linked ? 'success' : 'error'} sx={{ height: 40, mb: 2 }}>
              {loading && <Loader open />}
              {!linked && !loading && "Зв'язок не встановлено!"}
              {linked && !loading && "Зв'язок встановлено!"}
            </Alert>

            <Typography sx={{ mb: 1, mt: 1 }}>Каса</Typography>
            <FormTextField
              disabled={!!checkboxSettings.cashierToken}
              sx={{ mb: 3 }}
              label="Ключ ліцензії каси"
              name="checkboxSettings.key"
              fullWidth
              size="small"
            />

            <Typography sx={{ mb: 1 }}>Касир</Typography>

            {checkboxSettings.cashierName && (
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, ml: 1 }}>
                {checkboxSettings.cashierToken && <Check fontSize="small" color="success" sx={{ mr: 1 }} />}
                <Typography>{checkboxSettings.cashierName}</Typography>
              </Box>
            )}

            {checkboxSettings.cashierName && !linked && (
              <PrimaryButton onClick={logoutCashier}>Змінити касира</PrimaryButton>
            )}

            {!checkboxSettings.cashierToken && (
              <Box sx={{ maxWidth: 300 }}>
                <StyledTextField
                  label="Логін"
                  value={login}
                  fullWidth
                  size="small"
                  disabled={loading || !checkboxSettings.key}
                  onChange={(e) => setLogin(e.target.value)}
                  color="secondary"
                  sx={{ mb: 2 }}
                />

                <StyledTextField
                  label="Пароль"
                  value={password}
                  fullWidth
                  size="small"
                  type="password"
                  disabled={loading || !checkboxSettings.key}
                  onChange={(e) => setPassword(e.target.value)}
                  color="secondary"
                  sx={{ mb: 2 }}
                />

                <PrimaryButton
                  fullWidth
                  sx={{ mb: 2 }}
                  color="secondary"
                  disabled={loading || !login || !password}
                  variant="contained"
                  onClick={loginCashier}
                >
                  Авторизувати касира
                </PrimaryButton>
              </Box>
            )}

            {checkboxSettings.cashierToken && (
              <Box>
                <PrimaryButton
                  sx={{ mt: 3 }}
                  color={linked ? 'error' : 'secondary'}
                  disabled={loading}
                  variant={linked ? 'text' : 'contained'}
                  onClick={linked ? removeWebhook : setWebhook}
                >
                  {linked ? `Розірвати зв'язок` : `Встановити зв'язок`}
                </PrimaryButton>
              </Box>
            )}
          </Box>
        )}
      </Box>
    </EditModal>
  );
};

export default EditSelfOrderPoint;
